import { useAuth } from "@/core/hooks/useAuth";
import { useGestaoModulo } from "@/core/hooks/useGestaoModulo";
import { useMinisterios, useMinisteriosQueLidero } from "@/core/hooks/useMinisterios";
import type { MinisterioOpcao } from "@/modules/reservas/components/FormularioReserva";

/**
 * Opções de ministério para vincular a um evento/reserva — agora suporta
 * vínculo com MAIS de um ministério (F2-32, revisão 20/07/2026: atividade
 * conjunta entre ministérios).
 *
 * Quem é responsável pelo módulo (gestor OU líder do ministério vinculado a
 * Eventos/Reservas — `gerencia_modulo`, que desde F4-10 já inclui líder) ou
 * Admin da plataforma pode escolher QUALQUER ministério ativo da igreja —
 * EXCETO quando o módulo está em modo "individual" (F6-14, revisão
 * 21/07/2026, migration 0048): aí a escolha fica restrita aos ministérios
 * que a própria pessoa gerencia dentro do módulo (mais os que ela lidera),
 * já que a RLS também passou a recusar vincular um ministério fora desse
 * recorte. Todos os demais (nem admin, nem gestor do módulo) só podem
 * escolher entre os ministérios que eles mesmos lideram — o mesmo recorte
 * que a RLS aplica no banco (defesa em profundidade: a lista aqui só evita
 * mostrar opção que o banco recusaria).
 *
 * @param moduloCodigo "eventos" ou "reservas" — módulo do formulário que
 * está chamando este hook (precisa saber o modo de dados desse módulo).
 * @param podeGerirModulo `podeGerenciarEventos` ou `podeGerenciarReservas`
 * (conforme o formulário que está chamando este hook).
 */
export function useMinisterioOpcoes(
  moduloCodigo: "eventos" | "reservas",
  podeGerirModulo = false,
): MinisterioOpcao[] {
  const { ehAdminPlataforma } = useAuth();
  const { data: todos } = useMinisterios();
  const { data: liderados } = useMinisteriosQueLidero();
  const { modoDados, ministeriosGestao } = useGestaoModulo(moduloCodigo);

  if (ehAdminPlataforma) {
    return (todos ?? []).filter((m) => m.ativo).map((m) => ({ id: m.id, nome: m.nome }));
  }
  if (podeGerirModulo) {
    if (modoDados === "individual") {
      const permitidos = new Set([
        ...ministeriosGestao,
        ...(liderados ?? []).map((m) => m.id),
      ]);
      return (todos ?? [])
        .filter((m) => m.ativo && permitidos.has(m.id))
        .map((m) => ({ id: m.id, nome: m.nome }));
    }
    return (todos ?? []).filter((m) => m.ativo).map((m) => ({ id: m.id, nome: m.nome }));
  }
  return (liderados ?? []).map((m) => ({ id: m.id, nome: m.nome }));
}
